import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon, getEnvVariable } from '@appcharge/shared-ui';
import { useUserDetails } from 'api/useUserDetails';
import useFeatureFlags from 'api/useFeatureFlags';
import {
  EPublisherSolutionModel,
  EPublisherSolutionModelSubType,
  Project
} from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';

import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem
} from 'components/ui/sidebar';
import { Skeleton } from 'components/ui/skeleton';

import { cn } from '../../components/lib/utils';

import { SidebarGroupComponent } from './components';
import SidebarMenuItemChip from './components/SidebarMenuItemChip';
import { SidebarProvider, useSidebar } from './context';
import AppchargeIcon from './elements/AppchargeIcon';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenuButton
} from './elements/index';
import { useProjectInfo, useSidebarNavigation } from './hooks';
import { ESideBarContext } from './types';

// Main component that wraps the sidebar with context
export function AcSidebar() {
  const { publisherId } = useParams();
  const { featureFlags, companyFeatureFlags } = useFeatureFlags({
    publisherId
  });

  const { userDetails } = useUserDetails();

  return (
    <SidebarProvider
      initialUserDetails={userDetails}
      initialFeatureFlags={featureFlags}
      initialCompanyFeatureFlags={companyFeatureFlags}
    >
      <AcSidebarContent />
    </SidebarProvider>
  );
}

// Actual sidebar content component that uses the context
function AcSidebarContent() {
  const { context, navItems, userDetails, isLoading } = useSidebar();
  const { getProjectName, getPortalName, getPortalLevelStoreName } =
    useProjectInfo();
  const { publisherId, portalId } = useParams();
  const navigate = useNavigate();
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const { userDetails: sidebarUserDetails } = useUserDetails();
  const projectsDetails = sidebarUserDetails?.projects;
  const projectItem = projectsDetails?.find(
    (item: Project) => item.publisherId === publisherId
  );
  const isCheckout =
    projectItem?.projectType === EPublisherSolutionModel.CHECKOUT;
  const isSingleProject = projectsDetails?.length === 1;
  const isPaymentLinkProject =
    projectItem?.projectSubType ===
    EPublisherSolutionModelSubType.PAYMENT_LINKS;
  const portalItem = useMemo(
    () => userDetails?.portals?.find((portal) => portal.portalId === portalId),
    [userDetails?.portals, portalId]
  );
  const isParkingDomain = portalItem?.isParkingDomain || false;
  const shouldEnvIndicatorBeShown =
    getEnvVariable('REACT_APP_ENV') === 'sandbox';

  const handleBack = useCallback(() => {
    setIsNavigatingBack(true);
    const currentPortalId = userDetails?.portals?.find((portal) =>
      portal.stores.some((store) => store.storeId === publisherId)
    )?.portalId;

    if (context === ESideBarContext.PORTAL_LEVEL_STORE && currentPortalId) {
      navigate(`/portal/${currentPortalId}/settings/portal`);
    } else {
      navigate('../');
    }
  }, [context, navigate, publisherId, userDetails?.portals]);

  useEffect(() => {
    setIsNavigatingBack(false);
  }, [publisherId, portalId]);

  const title = useMemo(() => {
    if (isNavigatingBack) return '';

    const titleMap = {
      [ESideBarContext.COMPANY]:
        userDetails?.companyDisplayName || userDetails?.company || '',
      [ESideBarContext.PROJECT]: getProjectName(),
      [ESideBarContext.PORTAL]: getPortalName(),
      [ESideBarContext.PORTAL_LEVEL_STORE]: getPortalLevelStoreName(publisherId)
    };
    return titleMap[context] || '';
  }, [
    userDetails?.companyDisplayName,
    userDetails?.company,
    getProjectName,
    getPortalName,
    getPortalLevelStoreName,
    publisherId,
    context,
    isNavigatingBack
  ]);

  const isBackButtonVisible = useMemo(() => {
    const isProjectContext = context === ESideBarContext.PROJECT;

    if (isProjectContext) {
      return !(isSingleProject && isPaymentLinkProject);
    }

    return [
      ESideBarContext.PORTAL,
      ESideBarContext.PORTAL_LEVEL_STORE
    ].includes(context);
  }, [context, isSingleProject, isPaymentLinkProject]);

  // Setup navigation based on URL and context
  useSidebarNavigation();

  // Create a skeleton placeholder for nav items
  const renderSkeletons = () => {
    return Array(3)
      .fill(0)
      .map((_, index) => (
        <SidebarGroup key={`skeleton-${index}`}>
          <SidebarGroupLabel>
            <Skeleton className="h-4 w-24" />
          </SidebarGroupLabel>
          <SidebarMenu>
            {Array(index + 2)
              .fill(0)
              .map((_, itemIndex) => (
                <SidebarMenuItem key={`skeleton-item-${itemIndex}`}>
                  <div className="flex items-center gap-2 py-2 px-3 w-full">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </SidebarMenuItem>
              ))}
          </SidebarMenu>
        </SidebarGroup>
      ));
  };

  const titleChip = useMemo(() => {
    if (context !== ESideBarContext.COMPANY) {
      const currentId = publisherId || portalId;
      if (currentId) {
        const storedPageTypes = JSON.parse(
          localStorageUtil.getAny(ELocalStorageKeys.PROJECTS_PAGE_TYPES) || '{}'
        );

        return storedPageTypes[currentId];
      }
    }

    return undefined;
  }, [context, publisherId, portalId]);

  const titleBlockContent = (
    <>
      <AppchargeIcon
        context={context}
        isCheckout={isCheckout}
        projectSubType={projectItem?.projectSubType}
        isParkingDomain={isParkingDomain}
      />
      <span className="flex-1 min-w-0 truncate font-semibold">
        {title || <Skeleton className="h-4 w-32" />}
      </span>
      {titleChip && (
        <div className="shrink-0 mt-1">
          <SidebarMenuItemChip type={titleChip} />
        </div>
      )}
    </>
  );

  return (
    <Sidebar className={shouldEnvIndicatorBeShown ? 'top-8 h-[calc(100svh-2rem)]' : ''}>
      <SidebarContent className="p-6 gap-y-4">
        <img
          className={cn('logo w-[111px]')}
          alt="logo"
          src="./assets/images/ac-logo-purple.svg"
        />
        <SidebarGroup>
          <SidebarGroupLabel>Account</SidebarGroupLabel>
          <SidebarMenu className="gap-0">
            {isBackButtonVisible && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleBack}
                  className="flex items-center gap-x-2"
                >
                  <FontAwesomeIcon icon="fa-solid p-3 fa-arrow-left-to-bracket" />
                  Back
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            <SidebarMenuItem>
              {isSingleProject && isPaymentLinkProject ? (
                <div className="group/sidebar-menu-btn flex items-center gap-2 text-[14px] w-full">
                  {titleBlockContent}
                </div>
              ) : (
                <Link to="/" title="Go to Projects Page" className="w-full">
                  <SidebarMenuButton className="w-full">
                    {titleBlockContent}
                  </SidebarMenuButton>
                </Link>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        {isLoading
          ? renderSkeletons()
          : navItems.map((group, index) => (
              <SidebarGroupComponent
                key={`${group.title}-${index}`}
                group={group}
              />
            ))}
      </SidebarContent>
    </Sidebar>
  );
}

export default AcSidebar;
